import { useMutation, useQuery } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { GitPullRequest, ListChecks, LockKeyhole, Plus, ShieldCheck } from "lucide-react";
import { Link } from "react-router";
import type {
  PullRequestCheckState,
  RepositoryRole,
  WebhookState,
} from "@zworks/console-contracts";
import { gitHubAppPermissions } from "@zworks/console-contracts";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NotConnectedState,
} from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ApiError,
  gitHubConnectionQuery,
  pullRequestsQuery,
  repositoriesQuery,
  startGitHubInstall,
} from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { PageHeader } from "@/components/bespoke";

const roleLabels: Record<RepositoryRole, string> = {
  primary: "Primary",
  secondary: "Secondary",
};

const webhookBadges: Record<
  WebhookState,
  { label: string; variant: "success" | "warning" | "unknown" }
> = {
  delivering: { label: "Delivering", variant: "success" },
  failed: { label: "1 failed delivery", variant: "warning" },
  "not-verified": { label: "Not verified", variant: "unknown" },
};

const checkBadges: Record<
  PullRequestCheckState,
  { label: string; variant: "success" | "info" | "danger" | "unknown" }
> = {
  passed: { label: "All passed", variant: "success" },
  running: { label: "Running", variant: "info" },
  failed: { label: "Failed", variant: "danger" },
  "not-requested": { label: "Not requested", variant: "unknown" },
};

function PageHeading({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" } = {}) {
  return <PageHeader title="Repositories" headingLevel={headingLevel} />;
}

/** Repository inventory and pull request read models for the selected project. */
export function RepositoriesPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" } = {}) {
  const { project, loaded } = useCurrentProject();
  const repositories = useQuery({
    ...repositoriesQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const pullRequests = useQuery({
    ...pullRequestsQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const connection = useQuery({
    ...gitHubConnectionQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const install = useMutation({
    mutationFn: () => startGitHubInstall(project?.id ?? ""),
    onSuccess: (result) => {
      if (result.installUrl) window.open(result.installUrl, "_blank", "noopener,noreferrer");
    },
  });

  if (
    !loaded ||
    (project !== null && (repositories.isPending || pullRequests.isPending || connection.isPending))
  ) {
    return (
      <>
        <PageHeading headingLevel={headingLevel} />
        <LoadingState label="Loading repositories and pull requests" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <PageHeading headingLevel={headingLevel} />
        <EmptyState title="No projects yet" description="Create a project to bind repositories." />
      </>
    );
  }

  const failedQuery = repositories.isError
    ? repositories
    : pullRequests.isError
      ? pullRequests
      : connection.isError
        ? connection
        : null;
  if (failedQuery) {
    const error = failedQuery.error;
    return (
      <>
        <PageHeading headingLevel={headingLevel} />
        {error instanceof ApiError && error.status === 404 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Repositories could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => {
              void repositories.refetch();
              void pullRequests.refetch();
              void connection.refetch();
            }}
          />
        )}
      </>
    );
  }

  const repositoryRows = repositories.data ?? [];
  const pullRequestRows = pullRequests.data ?? [];
  const connectionState = connection.data ?? null;
  const configured = connectionState?.configured === true;

  return (
    <>
      <PageHeading headingLevel={headingLevel} />

      <section aria-labelledby="connect-repo-heading" className="mb-6 flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionHeading id="connect-repo-heading">Connect repository</SectionHeading>
          <Badge variant="outline">GitHub App</Badge>
        </div>
        <Card>
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-medium">Automatic repository connection</h3>
              <p className="text-xs text-muted-foreground">
                <Link
                  to="/integrations"
                  className="font-medium text-accent-text underline-offset-4 hover:underline"
                >
                  Integrations
                </Link>{" "}
                holds connection management. The App requests granular permissions: contents
                (write), pull requests (write), and metadata (read).
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Declared permissions:
              </span>
              <Badge variant="secondary">contents: {gitHubAppPermissions.contents}</Badge>
              <Badge variant="secondary">pull_requests: {gitHubAppPermissions.pullRequests}</Badge>
              <Badge variant="secondary">metadata: {gitHubAppPermissions.metadata}</Badge>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {configured ? (
                  <>
                    <Badge variant="success">Configured</Badge>
                    <span>
                      {connectionState?.appName ?? "GitHub App"} is configured. Install it on your
                      repositories, then connect one without SQL.
                    </span>
                  </>
                ) : (
                  <>
                    <Badge variant="unknown">Not connected yet</Badge>
                    <span>
                      GitHub App configuration is required before initiating install or OAuth flows.
                    </span>
                  </>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={!configured || install.isPending}
                title={
                  configured
                    ? "Open the GitHub App installation page"
                    : "GitHub App is not configured on the server"
                }
                onClick={() => install.mutate()}
              >
                <Plus aria-hidden className="mr-1.5 size-3.5" />
                {install.isPending ? "Opening install page" : "Connect repository"}
              </Button>
            </div>
            {install.isError ? (
              <p className="text-xs text-destructive-text" role="alert">
                The install page could not be opened. Try again shortly.
              </p>
            ) : null}
          </CardContent>
        </Card>
      </section>

      <section aria-labelledby="bound-repositories-heading" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionHeading id="bound-repositories-heading">Bound repositories</SectionHeading>
          <span className="text-sm text-muted-foreground">{repositoryRows.length} bound</span>
        </div>
        {repositoryRows.length === 0 ? (
          <EmptyState
            title="No repositories bound yet"
            description="Bind a repository to let runs read its code. Runs never receive write credentials."
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">Bound repositories and access state</caption>
                  <thead>
                    <tr>
                      {[
                        "Repository",
                        "Role",
                        "Branch",
                        "Access",
                        "Webhook",
                        "Branch protection",
                      ].map((label) => (
                        <th
                          key={label}
                          scope="col"
                          className="h-10 border-b px-3 text-left text-xs font-medium text-muted-foreground first:pl-5 last:pr-5"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {repositoryRows.map((repository) => {
                      const webhook = webhookBadges[repository.webhookState];
                      return (
                        <tr key={repository.id} className="group hover:bg-muted/50">
                          <td className="border-b px-3 py-3 first:pl-5 font-mono text-[0.8125rem]">
                            {repository.name}
                          </td>
                          <td className="border-b px-3 py-3">
                            <Badge variant="outline">{roleLabels[repository.role]}</Badge>
                          </td>
                          <td className="border-b px-3 py-3 font-mono">
                            {repository.defaultBranch}
                          </td>
                          <td className="border-b px-3 py-3">
                            <Badge variant="success">
                              <LockKeyhole aria-hidden className="size-3" />
                              Read-only clone
                            </Badge>
                          </td>
                          <td className="border-b px-3 py-3">
                            <Badge variant={webhook.variant}>{webhook.label}</Badge>
                          </td>
                          <td className="border-b px-3 py-3 last:pr-5">
                            {repository.branchProtection ? (
                              <Badge variant="success">{repository.branchProtection}</Badge>
                            ) : (
                              <Badge variant="unknown">Not verified</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="flex items-center gap-2 border-t px-5 py-3 text-xs text-muted-foreground">
                <LockKeyhole aria-hidden className="size-3.5 shrink-0" />
                Credentials are stored as references and never shown. Runs cannot push directly.
              </p>
            </CardContent>
          </Card>
        )}
      </section>

      <section aria-labelledby="merge-rules-heading" className="mt-8 flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionHeading id="merge-rules-heading">Merge and push rules</SectionHeading>
          <Badge variant="outline">Enforced by the server</Badge>
        </div>
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              <li className="flex items-start gap-3 px-5 py-3">
                <ListChecks aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Agents push only to zworks/ branches</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    A run commits on its own branch under zworks/ and can push only that branch.
                    Runs never receive write credentials.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 px-5 py-3">
                <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">An approved pull request merges</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    A merge goes through a brokered action that needs an approval. The author of a
                    change never decides their own approval, even with an eligible role.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 px-5 py-3">
                <GitPullRequest
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                />
                <div>
                  <p className="text-sm font-medium">Delete merged branches</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Remove the run branch once its pull request is merged, so the branch list stays
                    short and the next run starts from a clean base.
                  </p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </section>

      <section aria-labelledby="pull-requests-heading" className="mt-8 flex flex-col gap-3">
        <SectionHeading id="pull-requests-heading">Pull requests from runs</SectionHeading>
        {pullRequestRows.length === 0 ? (
          <EmptyState
            title="No pull requests from runs yet"
            description="Pull requests opened by project runs will appear here."
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table
                  className="w-full border-collapse text-sm"
                  aria-label="Pull requests from runs"
                >
                  <caption className="sr-only">Pull requests opened by runs</caption>
                  <thead>
                    <tr>
                      {["Pull request", "Run", "Head", "Checks", "Review", "Merge"].map((label) => (
                        <th
                          key={label}
                          scope="col"
                          className="h-10 border-b px-3 text-left text-xs font-medium text-muted-foreground first:pl-5 last:pr-5"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pullRequestRows.map((pr) => {
                      const checks = checkBadges[pr.checks];
                      const prHref = pr.url && pr.url.length > 0 ? pr.url : `#${pr.id}`;
                      return (
                        <tr key={pr.id} className="group hover:bg-muted/50">
                          <td className="max-w-sm border-b px-3 py-3 first:pl-5">
                            <a
                              className="inline-flex items-center gap-1 font-medium text-accent-text underline-offset-4 hover:underline"
                              href={prHref}
                              {...(pr.url && pr.url.length > 0
                                ? { target: "_blank", rel: "noreferrer" }
                                : {})}
                            >
                              <GitPullRequest aria-hidden className="size-4 shrink-0" />
                              {pr.title}
                            </a>
                            <p className="mt-1 font-mono text-xs text-muted-foreground">
                              {pr.head} into {pr.base}
                            </p>
                          </td>
                          <td className="border-b px-3 py-3 font-mono text-xs">
                            {pr.runId?.slice(0, 12) ?? "Unknown run"}
                          </td>
                          <td className="border-b px-3 py-3 font-mono">{pr.head.slice(0, 7)}</td>
                          <td className="border-b px-3 py-3">
                            <Badge variant={checks.variant}>
                              {pr.checksDetail ?? checks.label}
                            </Badge>
                          </td>
                          <td className="border-b px-3 py-3">
                            <Badge variant={pr.reviewState === "Approved" ? "success" : "unknown"}>
                              {pr.reviewState}
                            </Badge>
                          </td>
                          <td className="border-b px-3 py-3 last:pr-5">
                            <Badge
                              variant={
                                pr.mergeState === "Merged"
                                  ? "success"
                                  : pr.mergeState === "Not ready"
                                    ? "unknown"
                                    : "warning"
                              }
                            >
                              {pr.mergeState}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      <section aria-labelledby="continue-pr-heading" className="mt-8">
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div>
              <SectionHeading id="continue-pr-heading">Continue a pull request</SectionHeading>
            </div>
            <NotConnectedState
              title="Follow-up runs are not connected yet"
              description="Starting a run from a pull request needs the pull request integration first. The new run form does not read a pull request reference yet."
            />
          </CardContent>
        </Card>
      </section>
    </>
  );
}
